import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    BarChart3,
    Book,
    Camera,
    Clock,
    Cog,
    HelpCircle,
    Mail,
    Monitor,
    Search,
    Smartphone,
    UserRound,
    Users,
    Wrench,
} from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { SearchInput } from "@/components/ui/Form.jsx";
import { EmptyState } from "@/components/ui/Display.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { Blocks } from "@/components/docs/Blocks.jsx";

import { FEATURES, QUICK_START, TOPICS, topicText } from "@/content/docs.js";
import { cx } from "@/lib/cx.js";

/*
 * Replaces frontend/app/documentation/page.js and the eight app/docs/* routes.
 *
 * ONE PAGE, NOT NINE ROUTES. The old docs were a landing page linking to eight
 * separate pages, so answering "why is someone marked late?" meant guessing
 * which page it lived on and losing your place to find out. Everything is on
 * one page with a sticky contents list, and the search looks INSIDE the topics —
 * matching a phrase buried in a Q&A answer, not just a heading.
 *
 * The content is Bengali and stays Bengali; this is a port of the team's own
 * documentation. See src/content/docs.js.
 */
const ICONS = {
    book: Book,
    cog: Cog,
    monitor: Monitor,
    camera: Camera,
    chart: BarChart3,
    smartphone: Smartphone,
    wrench: Wrench,
    help: HelpCircle,
    mail: Mail,
    userRound: UserRound,
    team: Users,
    clock: Clock,
};

const Icon = ({ name, className }) => {
    const Cmp = ICONS[name] ?? Book;
    return <Cmp className={className} strokeWidth={2} aria-hidden="true" />;
};

export default function Documentation() {
    const [search, setSearch] = useState("");

    // Indexed once. topicText flattens a topic's every block, so a phrase
    // inside a collapsed answer is findable from the box at the top.
    const index = useMemo(() => TOPICS.map((t) => ({ topic: t, text: topicText(t) })), []);

    const query = search.trim().toLowerCase();
    const topics = useMemo(
        () => (query ? index.filter((e) => e.text.includes(query)).map((e) => e.topic) : TOPICS),
        [index, query],
    );

    return (
        <>
            <PageChrome breadcrumb={["Help & Docs"]} title="সহায়িকা ও ডকুমেন্টেশন" />

            <main className="mx-auto flex w-full max-w-[1500px] flex-col gap-5 p-4 lg:p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-[22px] font-semibold tracking-tight">
                            সহায়িকা ও ডকুমেন্টেশন
                        </h1>
                        <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                            Performance Track কীভাবে সেটআপ করবেন, ব্যবহার করবেন এবং সমস্যা হলে কী
                            করবেন।
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <SearchInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="সহায়িকায় খুঁজুন…"
                            aria-label="সহায়িকায় খুঁজুন"
                            className="w-64"
                            data-doc-search
                        />
                        <Button variant="primary" href="#topic-support-contact">
                            <Mail className="size-4" strokeWidth={2} />
                            সাপোর্ট
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[250px_minmax(0,1fr)]">
                    {/* ---- Contents ---- */}
                    <aside className="lg:sticky lg:top-20 lg:self-start">
                        <Card>
                            <CardHead>
                                <CardTitle icon={<Book className="size-4 text-slate-400" strokeWidth={2} />}>
                                    বিষয়সূচি
                                </CardTitle>
                            </CardHead>
                            <nav className="max-h-[70vh] overflow-y-auto p-2" aria-label="বিষয়সূচি">
                                {topics.length === 0 ? (
                                    <p className="px-2 py-3 text-[12.5px] text-slate-400 dark:text-slate-500">
                                        কিছু পাওয়া যায়নি।
                                    </p>
                                ) : (
                                    topics.map((t) => (
                                        <a
                                            key={t.key}
                                            href={`#topic-${t.key}`}
                                            data-topic-link={t.key}
                                            className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                                        >
                                            <Icon name={t.icon} className="size-4 shrink-0 text-slate-400" />
                                            <span className="min-w-0 truncate">{t.title}</span>
                                        </a>
                                    ))
                                )}
                            </nav>
                        </Card>
                    </aside>

                    <div className="flex min-w-0 flex-col gap-5">
                        {/* ---- Overview, hidden while searching ---- */}
                        {!query && (
                            <Card id="topic-overview" className="scroll-mt-20">
                                <CardBody className="space-y-5">
                                    <div>
                                        <Tag tone="brand">শুরু করুন</Tag>
                                        <h2 className="mt-3 text-[20px] font-semibold tracking-tight">
                                            এই অ্যাপটি কী?
                                        </h2>
                                        <p className="mt-2 text-[14px] leading-relaxed text-slate-600 dark:text-slate-300">
                                            Performance Track একটি কর্মী কার্যক্রম ট্র্যাকিং সিস্টেম।
                                            প্রতিটি কর্মীর কম্পিউটারে একটি ডেস্কটপ অ্যাপ চলে, যা কাজের
                                            সময়, ব্যবহৃত অ্যাপ ও ওয়েবসাইট, নিষ্ক্রিয় সময় এবং নির্দিষ্ট
                                            বিরতিতে স্ক্রিনশট রেকর্ড করে। অ্যাডমিন সবকিছু এই ওয়েব
                                            ড্যাশবোর্ডে দেখেন।
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="mb-3 text-[15px] font-semibold">চার ধাপে শুরু</h3>
                                        <ol className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                            {QUICK_START.map((step) => (
                                                <li
                                                    key={step.n}
                                                    className="flex flex-col rounded-lg border border-slate-200 p-3.5 dark:border-slate-800"
                                                >
                                                    <span className="grid size-7 place-items-center rounded-full bg-brand-600 text-[12px] font-semibold text-white">
                                                        {step.n}
                                                    </span>
                                                    <span className="mt-2.5 text-[13.5px] font-semibold">
                                                        {step.title}
                                                    </span>
                                                    <span className="mt-1 text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
                                                        {step.text}
                                                    </span>
                                                    <QuickLink step={step} />
                                                </li>
                                            ))}
                                        </ol>
                                    </div>

                                    <div>
                                        <h3 className="mb-3 text-[15px] font-semibold">
                                            মূল বৈশিষ্ট্যসমূহ
                                        </h3>
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                            {FEATURES.map((f) => (
                                                <div
                                                    key={f.title}
                                                    className="flex items-start gap-3 rounded-lg border border-slate-200 p-3.5 dark:border-slate-800"
                                                >
                                                    <span
                                                        className={cx(
                                                            "grid size-9 shrink-0 place-items-center rounded-lg",
                                                            f.tone,
                                                        )}
                                                    >
                                                        <Icon name={f.icon} className="size-4.5" />
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-[13.5px] font-semibold">
                                                            {f.title}
                                                        </span>
                                                        <span className="mt-0.5 block text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
                                                            {f.text}
                                                        </span>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {/* ---- Topics ---- */}
                        {topics.length === 0 ? (
                            <Card>
                                <CardBody>
                                    <EmptyState
                                        icon={<Search className="size-9 opacity-50" strokeWidth={1.5} />}
                                        title={`“${search}” — এই বিষয়ে কিছু পাওয়া যায়নি।`}
                                        action={
                                            <Button onClick={() => setSearch("")}>সার্চ মুছুন</Button>
                                        }
                                        data-no-results
                                    />
                                </CardBody>
                            </Card>
                        ) : (
                            topics.map((topic) => (
                                <Card
                                    key={topic.key}
                                    id={`topic-${topic.key}`}
                                    className="scroll-mt-20"
                                    data-topic={topic.key}
                                >
                                    <CardHead>
                                        <CardTitle
                                            icon={
                                                <Icon
                                                    name={topic.icon}
                                                    className="size-4 text-slate-400"
                                                />
                                            }
                                            hint={topic.blurb}
                                        >
                                            {topic.title}
                                        </CardTitle>
                                    </CardHead>
                                    <CardBody>
                                        <Blocks blocks={topic.blocks} />
                                    </CardBody>
                                </Card>
                            ))
                        )}
                    </div>
                </div>
            </main>
        </>
    );
}

/*
 * A quick-start step points either at an app route or at a topic further down
 * this page. The first is a router link; the second is a plain anchor, because
 * routing to a hash on the page you are already on is a scroll, not a
 * navigation.
 */
function QuickLink({ step }) {
    const className =
        "mt-2.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-600 transition hover:gap-1.5 dark:text-brand-400";

    const label = (
        <>
            {step.cta}
            <ArrowRight className="size-3.5" strokeWidth={2.2} />
        </>
    );

    if (step.href?.startsWith("#")) {
        return (
            <a href={step.href} className={className}>
                {label}
            </a>
        );
    }

    return (
        <Link to={step.href} className={className}>
            {label}
        </Link>
    );
}
